import React from "react";
import resStyles from "../css/scanningSessionResponsive.module.scss";
import { usePermission } from "context/PermissionContext";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";

interface MobileWorkflowSelectorProps {
  sessionData: any;
  toggleTab: (tab: "select" | "step1" | "step2") => void;
  handleUpdateStatus: (status: "paused" | "completed" | "active", shouldNavigate?: boolean) => void;
  step1CompletedSubs: number;
  fullyCompletedSubs: number;
  totalSubs: number;
}

const MobileScanningWorkflowSelector: React.FC<MobileWorkflowSelectorProps> = ({
  sessionData,
  toggleTab,
  handleUpdateStatus,
  step1CompletedSubs,
  fullyCompletedSubs,
  totalSubs,
}) => {
  const aliquotsPerSub = sessionData?.total_aliquots || 0;
  const totalAliquots = aliquotsPerSub * (sessionData?.totalsubjects ?? sessionData?.total_subjects ?? 0);
  const rawCompletedAliquots = sessionData?.completed_aliquots ?? 0;
  const completedAliquots = Math.min(rawCompletedAliquots, totalAliquots);

  const displayStep1Completed = Math.min(step1CompletedSubs, totalSubs);
  const displayStep2Completed = Math.min(fullyCompletedSubs, totalSubs);

  const { hasPermission } = usePermission();
  const permission1 = hasPermission(PERMISSION_MODULES.SESSION, PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER);
  const permission2 = hasPermission(PERMISSION_MODULES.SESSION, PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT);

  const isStep1Done = displayStep1Completed >= totalSubs && totalSubs > 0;
  const isStep2Done = (displayStep2Completed >= totalSubs || completedAliquots >= totalAliquots) && totalSubs > 0;

  let isSessionReadyToComplete = false;
  if (permission1 && permission2) {
    isSessionReadyToComplete = isStep1Done && isStep2Done;
  } else if (permission1) {
    isSessionReadyToComplete = isStep1Done;
  } else if (permission2) {
    isSessionReadyToComplete = isStep2Done;
  } else {
    isSessionReadyToComplete = fullyCompletedSubs >= totalSubs && totalSubs > 0;
  }

  return (
    <div className={resStyles.selectorContainer}>
      <div>
        <h5 className={resStyles.selectorTitle}>Select Workflow</h5>
        <p className={resStyles.selectorSubtitle}>
          Choose a task to proceed with scanning
        </p>
      </div>
      <div className={resStyles.workflowCards}>
        {permission1 && (
          <div
            className={resStyles.workflowCard}
            onClick={() => toggleTab("step1")}
          >
            <div className={resStyles.cardIconWrapper}>
              <i className="ri-user-shared-line"></i>
            </div>
            <div>
              <h6 className={resStyles.cardTitle}>Subject to Vacutainer</h6>
              <p className={resStyles.cardDesc}>
                Scan subject and match to corresponding vacutainer.
              </p>
            </div>
            <div className={resStyles.cardProgressInfo}>
              <span className={resStyles.progressLabel}>Completed</span>
              <span className={resStyles.progressValue}>
                {displayStep1Completed} / {totalSubs}
              </span>
            </div>
          </div>
        )}
        {
          permission2 && (
            <div
              className={resStyles.workflowCard}
              onClick={() => toggleTab("step2")}
            >
              <div
                className={`${resStyles.cardIconWrapper} ${resStyles.step2Icon}`}
              >
                <i className="ri-flask-line"></i>
              </div>
              <div>
                <h6 className={resStyles.cardTitle}>Vacutainer to Aliquots</h6>
                <p className={resStyles.cardDesc}>
                  Scan vacutainer and separate into aliquot samples.
                </p>
              </div>
              <div className={resStyles.cardProgressInfo}>
                <span className={resStyles.progressLabel}>Completed</span>
                <span className={resStyles.progressValue}>
                  {displayStep2Completed} / {totalSubs}
                </span>
              </div>
            </div>
          )}
      </div>

      {/* Complete Session Button */}
      <div className="mt-4 mb-2">
        <button
          type="button"
          onClick={() => handleUpdateStatus("completed", true)}
          disabled={!isSessionReadyToComplete}
          className="btn w-100 py-3 d-flex align-items-center justify-content-center gap-2 border-0 shadow-sm"
          style={{
            borderRadius: "12px",
            backgroundColor: isSessionReadyToComplete ? "#10b981" : "#e2e8f0",
            color: isSessionReadyToComplete ? "#ffffff" : "#94a3b8",
            fontWeight: "bold",
            fontSize: "15px",
            transition: "all 0.2s ease",
            cursor: isSessionReadyToComplete ? "pointer" : "not-allowed"
          }}
        >
          <i className="ri-checkbox-circle-fill fs-18"></i>
          Complete Session
        </button>
      </div>
    </div>
  );
};

export default MobileScanningWorkflowSelector;
